/**
 * One answer, and the honest account of what happened to it.
 *
 * The mockups counted dropped claims three different ways on one screen — "1 dihapus"
 * next to "4 of 5" next to "2 pernyataan dihapus". For a product whose whole pitch is
 * that it counts its failures out loud, the demo could not count. There is exactly ONE
 * number here and it comes from `dropped_claims_count` on the response.
 *
 * Ordering, per the agreed model: the Policy Guard clears before any byte ships; the
 * source check lands LAST and can only annotate. Once text is on screen you cannot
 * unshow it, so an unsupported claim is MARKED, never silently deleted — and the note
 * says the check ran after the text was sent.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { citationKey } from "@/lib/api-types.ts";
import type { Citation, HistoryWindow, QueryResponse } from "@/lib/api-types.ts";
import { annotateForCopy, placeV2Claims, type QueryMode, type V2Annotation } from "@/lib/queryV2.ts";
import { AgentTrace, type TraceStep } from "./AgentTrace.tsx";
import { AnswerMarkdown, type ClaimMark } from "./AnswerMarkdown.tsx";
import { V2AnswerBody, V2FootNote } from "./AnswerV2.tsx";
import { Button, Panel } from "./primitives.tsx";
import { v2CopyLabel } from "./v2Labels.ts";

const NO_INDEX: Map<string, number> = new Map();
const NO_ANNOTATION: V2Annotation = {};

export interface AnswerTurn {
  id: string;
  question: string;
  response?: QueryResponse;
  /** Citation numbers as they appear in the conversation-wide evidence rail. */
  citationIndex: Map<string, number>;
  /** Terminal SSE metadata also exists when the Policy Guard withheld answer text. */
  historyWindow?: HistoryWindow;
  guardBlocked?: { reason: string };
  error?: string;
  stopped?: boolean;
  scopeSentence: string;
  /** Set when the source check arrived after the text was already on screen. */
  lateGate?: boolean;
  /** Wall-clock the run took, kept so the reopened trail still reports it. */
  elapsedSeconds?: number;
  /** Rebuilt from stored messages rather than answered in this session. */
  replayed?: boolean;
  /** The ordered tool trace, which survives a reload only in
   *  `messages[].metadata.synthesis.tools_used`. */
  toolsUsed?: string[];
  /** The live steps this turn produced, kept AFTER the answer lands so the trail can be
   *  reopened. The wireframe's "Jejak penelusuran" — a receipt you can expand, not a
   *  dead line of text. */
  steps?: TraceStep[];
  /** The route that answered. Absent means v1, which every turn was before /query/v2. */
  mode?: QueryMode;
  /** /query/v2 only: the claims and their verdict counts. */
  v2?: V2Annotation;
  /** The server has no /query/v2 route (its flag is off); the turn offers v1 instead. */
  v2Unavailable?: boolean;
}

export function Answer({
  turn,
  litPassage,
  onPassageClick,
  references,
  claims,
  evidenceIndex,
  onNewChat,
  newChatDisabled = false,
  onAskV1,
}: {
  turn: AnswerTurn;
  litPassage: number | null;
  onPassageClick: (passage: number | null) => void;
  /** `legal_reference` -> evidence number, so "Pasal 19" in the prose is clickable.
   *  NAVIGATION only — the claim's provenance is `claims` below (#532). */
  references?: Map<string, number>;
  /** The authoritative claim-to-passage marks, resolved from the response's claim map. */
  claims?: ClaimMark[];
  /** chunk id -> the number on the rail, CONVERSATION-wide. What the reader actually saw. */
  evidenceIndex?: Map<string, number>;
  onNewChat?: () => void | Promise<void>;
  newChatDisabled?: boolean;
  /** Re-asks this turn's question on v1, offered when the server has no /query/v2. */
  onAskV1?: () => void;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  // Collapsed once the answer is here: the trail is reference, not the point. It stays
  // one click away rather than being thrown away.
  const [traceOpen, setTraceOpen] = useState(false);
  const response = turn.response;
  const historyWindow = turn.historyWindow ?? response;
  const sourceCitations = response?.citations.filter(
    (citation) => citation.kind === "chunk" || citation.kind === "tool",
  ) ?? [];
  // No annotation on a v2 turn means no verdict arrived: rendered as unchecked.
  const v2 = turn.mode === "v2" ? (turn.v2 ?? NO_ANNOTATION) : undefined;
  const v2Placement = useMemo(
    () =>
      v2 && response
        ? placeV2Claims(response.answer, v2.claims ?? [], evidenceIndex ?? NO_INDEX)
        : { marks: [], unplaced: [] },
    [v2, response, evidenceIndex],
  );

  async function copyWithSources() {
    if (!response) return;
    // The real workflow is pasting into a memo, and a paste that loses its citations
    // is how an attributed claim becomes an unattributed one.
    const endnotes = buildEndnotes(response.citations, evidenceIndex);
    // A v2 paste keeps each unverified claim's label in the text: away from this screen,
    // an unmarked sentence reads as checked.
    const body = v2
      ? annotateForCopy(response.answer, v2Placement.marks, v2CopyLabel(t))
      : response.answer;
    const text = endnotes ? `${body}\n\n${t("answer.sources")}:\n${endnotes}` : body;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  }

  return (
    <div className="answer">
      <div className="ask">
        <div className="eyebrow ask-label">{t("chat.you")}</div>
        <p className="ask-text">{turn.question}</p>
      </div>

      {turn.guardBlocked ? (
        // A calm panel, not a red toast. A refusal is a result, not a crash.
        <Panel tone="amber" title={t("answer.guardBlocked")}>
          {t("answer.guardBody")}
        </Panel>
      ) : null}

      {turn.v2Unavailable ? (
        <Panel tone="amber" title={t("errors.v2Unavailable")}>
          <p>{t("errors.v2UnavailableBody")}</p>
          {onAskV1 ? (
            <Button size="sm" onClick={onAskV1} disabled={newChatDisabled}>
              {t("errors.askWithV1")}
            </Button>
          ) : null}
        </Panel>
      ) : turn.error ? (
        <Panel tone="red" title={t("errors.generic")}>
          {turn.error}
        </Panel>
      ) : null}

      {turn.stopped ? (
        <Panel tone="slate" title={t("chat.stopped")} />
      ) : null}

      {/* The completed trail, above the answer, exactly where the wireframe puts it. */}
      {turn.steps && turn.steps.length > 0 ? (
        <AgentTrace
          steps={turn.steps}
          elapsedSeconds={turn.elapsedSeconds ?? 0}
          running={false}
          collapsed={!traceOpen}
          onToggle={() => setTraceOpen((open) => !open)}
        />
      ) : null}
      {historyWindow?.history_truncated === true ? (
        <Panel tone="slate" title={t("answer.historyWindowTitle")}>
          <p>
            {t("answer.historyWindowBody", { count: historyWindow.history_exchanges_in_view })}
          </p>
          {onNewChat ? (
            <Button size="sm" onClick={() => void onNewChat()} disabled={newChatDisabled}>
              {t("chat.newChat")}
            </Button>
          ) : null}
        </Panel>
      ) : null}


      {response ? (
        <>
          <div className="answer-head">
            <span className="eyebrow">{t("answer.heading")}</span>
          </div>

          {v2 ? (
            <V2AnswerBody
              response={response}
              annotation={v2}
              marks={v2Placement.marks}
              unplaced={v2Placement.unplaced}
              litPassage={litPassage}
              onPassageClick={onPassageClick}
              references={references}
            />
          ) : null}

          {!v2 && response.fallback_used && response.fallback_reason ? (
            <Panel
              tone="amber"
              title={
                response.fallback_reason === "no_claims"
                  ? t("answer.fallbackTitleNoClaims")
                  : response.fallback_reason === "unverified_no_claims"
                    ? t("answer.fallbackTitleUnverifiedNoClaims")
                    : response.fallback_reason === "coverage_incomplete"
                      ? t("answer.fallbackTitleCoverageIncomplete")
                      : response.fallback_reason === "unresolved_citation"
                        ? t("answer.fallbackTitleUnresolvedCitation")
                        : response.fallback_reason === "unparseable_completion"
                          ? t("answer.fallbackTitleUnparseableCompletion")
                          : response.fallback_reason === "over_refusal"
                            ? t("answer.fallbackTitleOverRefusal")
                            : response.fallback_reason === "agent_output_limit"
                              ? t("answer.fallbackTitleAgentOutputLimit")
                              : t("answer.fallbackTitleUnknown")
              }
            >
              {response.fallback_reason === "no_claims"
                ? t("answer.fallbackNoClaims")
                : response.fallback_reason === "unverified_no_claims"
                  ? t("answer.fallbackUnverifiedNoClaims")
                  : response.fallback_reason === "coverage_incomplete"
                    ? t("answer.fallbackCoverageIncomplete")
                    : response.fallback_reason === "unresolved_citation"
                      ? t("answer.fallbackUnresolvedCitation")
                      : response.fallback_reason === "unparseable_completion"
                        ? t("answer.fallbackUnparseableCompletion")
                        : response.fallback_reason === "over_refusal"
                          ? t("answer.fallbackOverRefusal")
                          : response.fallback_reason === "agent_output_limit"
                            ? t("answer.fallbackAgentOutputLimit")
                            : t("answer.fallbackUnknown")}
            </Panel>
          ) : null}

          {/* A partial answer is published, not downgraded: every statement shown passed
              the source check, and the note at the end of the answer names what the
              documents could not answer. Slate, not amber — amber stays the zero-claim
              "Belum diverifikasi" state. */}
          {!v2 && !response.fallback_used && response.fallback_reason === "partial_answer" ? (
            <Panel tone="slate" title={t("answer.partialTitle")}>
              {t("answer.partialBody")}
            </Panel>
          ) : null}

          {v2 ? null : (
            <AnswerMarkdown
              litPassage={litPassage}
              onPassageClick={onPassageClick}
              references={references}
              claims={claims}
            >
              {response.answer}
            </AnswerMarkdown>
          )}

          {/* Print carries its own legend: the mockup print sheet hid the evidence
              rail but kept the chips, so a printed answer had [1] [2] and nothing
              to resolve them against. */}
          {/* NUMBERED AS THE READER SAW THEM, via `value` — not 1..n. The chips in the
              printed prose carry the CONVERSATION-WIDE rail number (`AnswerMarkdown`
              renders `references`), so a plain <ol> printed turn 2 with "…Nomor 10 Tahun
              2018 [4]" above a legend calling it [1]. Exactly the mismatch `buildEndnotes`
              fixed for COPY; the print path was left on the broken numbering and this list
              was edited without noticing. Sorted so the legend reads in the order the
              marks do. */}
          {sourceCitations.length > 0 ? (
            <ol className="print-endnotes">
              {sourceCitations
                .map((citation, index) => ({
                  citation,
                  shown: evidenceIndex?.get(citationKey(citation)) ?? index + 1,
                }))
                .sort((a, b) => a.shown - b.shown)
                .map(({ citation, shown }) => (
                  <li key={citationKey(citation)} value={shown}>
                    {describeCitation(citation)}
                  </li>
                ))}
            </ol>
          ) : null}

          <div className="answer-foot">
            {/* A zero drop count covers only records the model submitted. "All supported"
                additionally needs the backend's complete answer-to-provenance verdict;
                missing maps, unplaceable claims, coverage gaps, and unresolved citations
                all stay UNKNOWN rather than becoming a clean bill of health. */}
            {v2 ? (
              <V2FootNote annotation={v2} unplaced={v2Placement.unplaced} />
            ) : response.dropped_claims_count > 0 &&
            !(response.fallback_used && response.fallback_reason === null) ? (
              <span className="foot-note">
                {t("answer.dropped", { count: response.dropped_claims_count })}
              </span>
            ) : response.dropped_claims_count === 0 &&
              response.provenance_complete &&
              response.claim_map !== undefined ? (
              <span className="foot-note">{t("answer.droppedNone")}</span>
            ) : (
              <span className="foot-note faint">{t("answer.gateUnknown")}</span>
            )}
            {turn.lateGate ? <span className="foot-note faint">{t("answer.lateGate")}</span> : null}
            <span className="foot-actions">
              <Button size="sm" onClick={copyWithSources}>
                {copied ? t("answer.copied") : t("answer.copyWithSources")}
              </Button>
              <Button size="sm" onClick={() => window.print()}>
                {t("answer.print")}
              </Button>
            </span>
          </div>

          {/* Each turn is stamped with the scope it ran under. A replayed turn has no
              stored scope, so it says that instead of printing an empty stamp. */}
          <p className="hint" style={{ marginTop: "var(--s2)" }}>
            {turn.scopeSentence
              ? t("scope.stamp", { scope: turn.scopeSentence })
              : t("scope.stampUnknown")}
          </p>

          {/* No static receipt line here any more: it duplicated the expandable trail
              above and could not be opened, which is what made it read as dead text. */}
          {!turn.steps?.length && turn.toolsUsed && turn.toolsUsed.length > 0 ? (
            // A REPLAYED turn: the steps are gone, only `tools_used` survived the
            // database. Say what is known and no more.
            <p className="hint">
              {t("trace.receipt", { count: turn.toolsUsed.length })} ·{" "}
              <span className="mono">{summariseTools(turn.toolsUsed)}</span>
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/** "search_documents ×12" rather than twelve identical lines. */
export function summariseTools(tools: string[]): string {
  const counts = new Map<string, number>();
  for (const tool of tools) counts.set(tool, (counts.get(tool) ?? 0) + 1);
  return [...counts.entries()]
    .map(([tool, count]) => (count > 1 ? `${tool} ×${count}` : tool))
    .join(" · ");
}

/**
 * The endnotes a copied answer carries, numbered AS THE READER SAW THEM.
 *
 * This used to number 1..n within the turn, while the evidence rail numbers across the
 * whole conversation — so copying turn 2 produced "[1]" for a source shown on screen as
 * 4. The same mismatch the inline markers had, in the one place it cannot be noticed:
 * once the text is in a memo, there is no rail to check it against.
 *
 * Sorted by the number shown, so the list reads in the order the marks do. A citation
 * whose chunk is not on the rail keeps its positional number rather than being dropped —
 * an unnumbered source is still a source, and losing it is how an attributed claim
 * becomes an unattributed one.
 */
export function buildEndnotes(
  citations: ReadonlyArray<Citation>,
  evidenceIndex?: Map<string, number>,
): string {
  return citations
    .filter((citation) => citation.kind === "chunk" || citation.kind === "tool")
    .map((citation, index) => ({
      shown: evidenceIndex?.get(citationKey(citation)) ?? index + 1,
      text: describeCitation(citation),
    }))
    .sort((a, b) => a.shown - b.shown)
    .map((entry) => `[${entry.shown}] ${entry.text}`)
    .join("\n");
}

export function describeCitation(citation: Citation): string {
  // A conversation record, not a document: stored rows still carry it, but it
  // has no title, chunk, or offsets. Endnotes only list documents, so this is
  // a stable label rather than a throw on `section_path.length`.
  if (citation.kind === "history") {
    const turn =
      typeof citation.turn_index === "number" ? ` · pesan ${citation.turn_index + 1}` : "";
    return `Dari percakapan ini${turn}`;
  }
  if (citation.kind === "tool") {
    // Portal citations carry a backend-authored display title assembled from the exact
    // request-local dataset/indicator, portal and period. Prefer it when it is more
    // specific than the legacy tool-name fallback; the raw call remains audit detail.
    if (citation.document_title && citation.document_title !== citation.tool) {
      return citation.document_title;
    }
    // THE CALL, not the tool name. Every other field on a live read is null, so the
    // generic path below rendered the endnote as bare "open_data_read" — which names the
    // pipe and not the read, and cannot be re-run. `open_data_read({"handle": "ckan:…"})`
    // is what a reader repeats to check the figure, and it is what makes a pasted answer
    // still checkable away from the app. Already bounded to 500 chars by the API.
    //
    // `||`, NOT `??`. The API always sends `call` as a STRING — `str(values.get("call",
    // ""))[:500]` in `ToolGrounding.citation` — so it is `""` at worst and never null. `??`
    // does not fall back on empty, which made this print "[1] " with nothing after it:
    // worse than the bare tool name it replaced, and the fallback the ledger advertised
    // was unreachable. A reviewer found it; the test that "covered" it passed `undefined`,
    // a shape the API cannot emit.
    return citation.call || citation.tool || citation.document_title;
  }
  const bits = [citation.document_title];
  if (citation.legal_reference) bits.push(citation.legal_reference);
  if (citation.page_number !== null) bits.push(`hlm. ${citation.page_number}`);
  if (citation.section_path.length > 0) bits.push(citation.section_path.join(" › "));
  return bits.join(" · ");
}
